import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { POSTS, formatDate } from "../content/posts";
import { CATEGORIES } from "../content/categories";
import { PostCard } from "../components/PostCard";
import { GradeUnderline } from "../components/GradeUnderline";
import { categoryBySlug } from "../content/categories";
import { initSignatureAnimation, initHomeAnimations } from "../lib/animations";
import { subscribeToNewsletter } from "../lib/newsletter.functions";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Acadly — Study Notes & Exam Strategy for DU Students" },
      {
        name: "description",
        content:
          "Subject explainers, exam strategy, and study systems for Indian undergraduates. Economics-forward, written by someone who sat the same exams.",
      },
      {
        name: "robots",
        content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      { property: "og:site_name", content: "Acadly" },
      { property: "og:locale", content: "en_IN" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Acadly — Study Notes & Exam Strategy for DU Students" },
      {
        property: "og:description",
        content:
          "Subject explainers, exam strategy, and study systems for Indian undergraduates. Economics-forward, written by someone who sat the same exams.",
      },
      { property: "og:url", content: "https://acadly.in/" },
      { property: "og:image", content: "https://acadly.in/og-default.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Acadly — Study Notes & Exam Strategy for DU Students" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Acadly — Study Notes & Exam Strategy for DU Students" },
      {
        name: "twitter:description",
        content:
          "Subject explainers, exam strategy, and study systems for Indian undergraduates. Economics-forward, written by someone who sat the same exams.",
      },
      { name: "twitter:image", content: "https://acadly.in/og-default.png" },
      { name: "twitter:image:alt", content: "Acadly — Study Notes & Exam Strategy for DU Students" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/" }],
  }),
  component: Home,
});

function Home() {
  useEffect(() => {
    void initSignatureAnimation();
    void initHomeAnimations();
  }, []);

  const subscribe = useServerFn(subscribeToNewsletter);
  const [subState, setSubState] = useState<
    | { state: "idle" }
    | { state: "sending" }
    | { state: "sent" }
    | { state: "error"; message: string }
  >({ state: "idle" });

  const newsPosts = POSTS.filter((p) => p.meta.category === "news").slice(0, 4);
  const nonNews = POSTS.filter((p) => p.meta.category !== "news");
  const featured = nonNews[0] ?? POSTS[0];
  const rest = nonNews.slice(1, 7);

  return (
    <div className="paper-grain">
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pt-16 pb-20 md:pt-24 md:pb-28">
        <div data-hero-item className="stamp mb-8">Vol. 01 · Acadly Notes</div>
        <h1
          data-hero-item
          className="text-5xl md:text-7xl leading-[0.95] tracking-tight font-semibold max-w-4xl"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Study notes that actually help you sit
          {" "}
          <span className="relative inline-block">
            <span>the exam</span>
            <GradeUnderline className="absolute left-0 -bottom-2" />
          </span>
          .
        </h1>
        <p data-hero-item className="mt-8 max-w-2xl text-lg md:text-xl text-pencil leading-relaxed">
          Explainers, exam strategy and honest college guidance for Indian
          undergraduates. Written by someone who sat the same papers - mostly
          economics, and mostly for people who read the textbook and still feel
          lost.
        </p>
        <div data-hero-item className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/blog"
            className="mono-label bg-ink text-paper px-5 py-3 hover:bg-grade transition-colors"
          >
            Read the archive →
          </Link>
          <Link
            to="/about"
            className="mono-label border border-ink px-5 py-3 hover:border-grade hover:text-grade transition-colors"
          >
            Who writes this
          </Link>
        </div>
      </section>

      {/* Category index tabs */}
      <section
        data-reveal
        className="border-t border-ink"
      >
        <div className="mx-auto max-w-6xl px-5 py-14">
          <div className="flex items-center justify-between mb-8">
            <div className="mono-label">The Sections</div>
            <div className="mono-label hidden sm:block">Five running threads</div>
          </div>
          <ul data-reveal-stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <li key={c.slug} data-reveal-item className="group">
                <Link
                  to="/category/$category"
                  params={{ category: c.slug }}
                  className="block h-full border border-rule bg-card p-6 hover:border-grade transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="mono-label text-grade">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="mono-label">Section</span>
                  </div>
                  <h2
                    className="mt-4 text-2xl leading-snug font-semibold"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {c.label}
                  </h2>
                  <p className="mt-2 text-sm text-pencil leading-relaxed">
                    {c.blurb}
                  </p>
                  <div className="mt-4 mono-label group-hover:text-grade transition-colors">
                    Enter →
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Featured piece */}
      {featured && (
        <FrontPage meta={featured.meta} />
      )}

      {/* Latest news */}
      {newsPosts.length > 0 && (
        <section data-reveal className="border-t border-ink bg-card">
          <div className="mx-auto max-w-6xl px-5 py-20">
            <div className="flex items-baseline justify-between mb-10 pb-6 border-b border-ink/15">
              <div>
                <div className="mono-label text-grade mb-2">
                  <span className="inline-block w-6 h-px bg-grade align-middle mr-3" />
                  The Wire
                </div>
                <h2
                  className="text-3xl md:text-4xl font-semibold tracking-tight"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Latest from Delhi University
                </h2>
              </div>
              <Link
                to="/category/$category"
                params={{ category: "news" }}
                className="mono-label hover:text-grade transition-colors hidden sm:inline"
              >
                All news →
              </Link>
            </div>
            <div data-reveal-stagger className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
              {newsPosts.map((p) => (
                <div key={p.meta.slug} data-reveal-item>
                  <PostCard meta={p.meta} />
                </div>
              ))}
            </div>
            <div className="mt-10 sm:hidden">
              <Link
                to="/category/$category"
                params={{ category: "news" }}
                className="mono-label border border-ink px-4 py-2 hover:text-grade hover:border-grade"
              >
                All news →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Recent posts */}
      <section data-reveal className="mx-auto max-w-6xl px-5 py-16">
        <div className="flex items-baseline justify-between mb-10">
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Recent notes
          </h2>
          <Link to="/blog" className="mono-label hover:text-grade transition-colors">
            All posts →
          </Link>
        </div>
        <div data-reveal-stagger className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <div key={p.meta.slug} data-reveal-item>
              <PostCard meta={p.meta} />
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter / editorial closer */}
      <section data-reveal className="border-t border-ink bg-ink text-paper">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="min-w-0">
            <div className="mono-label text-grade-soft mb-4">Editor's note</div>
            <p
              className="text-xl sm:text-2xl md:text-3xl leading-snug break-words"
              style={{ fontFamily: "var(--font-display)" }}
            >
              "The point of these notes is not to replace your textbook. It's to
              give you the reading of the textbook that a slightly older student
              would have handed you, three months before the exam, if you'd
              known to ask."
            </p>
            <div className="mt-6 mono-label">Dhairya, founder</div>
          </div>
          <div className="min-w-0 border border-paper/25 p-6">
            <div className="mono-label text-grade-soft">Get new pieces</div>
            <p className="mt-3 text-paper/85 leading-relaxed">
              A note goes out when a new piece lands. No spam, unsubscribe in
              one click.
            </p>
            {subState.state === "sent" ? (
              <p className="mt-5 mono-label text-grade-soft">
                You're on the list. Check your inbox for a confirmation email.
              </p>
            ) : (
              <form
                className="mt-5 flex flex-wrap gap-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const fd = new FormData(form);
                  const email = String(fd.get("email") ?? "");
                  const website = String(fd.get("website") ?? "");
                  setSubState({ state: "sending" });
                  try {
                    await subscribe({ data: { email, website } });
                    setSubState({ state: "sent" });
                    form.reset();
                  } catch (err) {
                    const message =
                      err instanceof Error
                        ? err.message
                        : "Could not subscribe. Please try again.";
                    setSubState({ state: "error", message });
                  }
                }}
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] w-px h-px overflow-hidden"
                />
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@college.edu"
                  aria-label="Email address"
                  className="min-w-0 flex-1 basis-full sm:basis-0 bg-transparent border border-paper/40 px-3 py-2 text-paper placeholder:text-paper/50 focus:border-grade-soft outline-none"
                />
                <button
                  type="submit"
                  disabled={subState.state === "sending"}
                  className="shrink-0 mono-label bg-paper text-ink px-4 py-2 hover:bg-grade hover:text-paper transition-colors disabled:opacity-50"
                >
                  {subState.state === "sending" ? "Sending…" : "Subscribe"}
                </button>
                {subState.state === "error" && (
                  <p className="basis-full mono-label text-grade-soft">
                    {subState.message}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FrontPage({ meta }: { meta: (typeof POSTS)[number]["meta"] }) {
  const cat = categoryBySlug(meta.category);
  const thumb = `/blog/${meta.slug}.jpg`;
  return (
    <section
      data-reveal
      className="relative border-t border-ink/15 bg-paper"
    >
      <div className="mx-auto max-w-6xl px-5 py-24 md:py-32">
        {/* Masthead — a single hairline row */}
        <div className="flex items-baseline justify-between pb-6 border-b border-ink/15">
          <span className="mono-label tracking-[0.2em]">The Front Page</span>
          <span className="mono-label text-pencil hidden sm:inline">
            No. {String(POSTS.length).padStart(2, "0")} / {formatDate(meta.pubDate)}
          </span>
        </div>

        <div className="grid gap-14 md:gap-20 md:grid-cols-12 items-start pt-16 md:pt-20">
          {/* Copy side — leads on desktop */}
          <div className="md:col-span-7 md:order-1 order-2">
            <div className="mono-label text-grade mb-6">
              <span className="inline-block w-6 h-px bg-grade align-middle mr-3" />
              {cat?.label ?? meta.category} · {meta.readMinutes} min
            </div>
            <h2
              className="text-4xl md:text-5xl lg:text-[3.75rem] leading-[1.05] tracking-tight font-normal text-balance"
              style={{ fontFamily: "var(--font-display)" }}
            >
              <Link
                to="/blog/$slug"
                params={{ slug: meta.slug }}
                className="hover:text-grade transition-colors"
              >
                {meta.title}
              </Link>
            </h2>
            <p className="mt-8 text-lg text-pencil leading-relaxed max-w-xl">
              {meta.description}
            </p>

            <div className="mt-10">
              <Link
                to="/blog/$slug"
                params={{ slug: meta.slug }}
                className="group inline-flex items-center gap-3 mono-label border-b border-ink pb-1 hover:text-grade hover:border-grade transition-colors"
              >
                Read the piece
                <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
            </div>
          </div>

          {/* Image side */}
          <Link
            to="/blog/$slug"
            params={{ slug: meta.slug }}
            aria-label={meta.title}
            className="group md:col-span-5 md:order-2 order-1 block"
          >
            <div className="relative overflow-hidden bg-card">
              <img
                data-parallax="30"
                src={thumb}
                alt={`Illustration for ${meta.title}`}
                width={1200}
                height={1500}
                loading="eager"
                fetchPriority="high"
                className="w-full h-auto aspect-[4/5] object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
              />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
